// components/RecordingStudio/ProjectsDrawer.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  X,
  Plus,
  Folder,
  Trash2,
  Edit2,
  Music2,
  Clock,
  Layers,
  ChevronRight,
  Loader,
  FolderOpen,
} from "lucide-react";
import api from "../../services/api";

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const formatDuration = (sec) => {
  if (!sec || sec <= 0) return null;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return m > 0 ? `${m}:${s.toString().padStart(2, "0")}` : `${s}s`;
};

const RenameInput = ({ value, onSave, onCancel }) => {
  const [name, setName] = useState(value);
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const commit = () => {
    if (name.trim()) onSave(name.trim());
    else onCancel();
  };
  return (
    <input
      ref={ref}
      value={name}
      onChange={(e) => setName(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") onCancel();
        e.stopPropagation();
      }}
      onClick={(e) => e.stopPropagation()}
      className="flex-1 min-w-0 rounded-lg px-2.5 py-1 text-sm text-white focus:outline-none"
      style={{
        background: "rgba(139,92,246,0.12)",
        border: "1px solid rgba(139,92,246,0.4)",
      }}
    />
  );
};

const ProjectRow = ({ project, isActive, onOpen, onRename, onDelete }) => {
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const blockCount = project.blocks?.length ?? project.blockCount ?? 0;
  const dur = formatDuration(project.duration);

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${project.name}"? This cannot be undone.`))
      return;
    setDeleting(true);
    try {
      await onDelete(project.id);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      onClick={() => !renaming && onOpen(project.id)}
      className="group relative flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-150"
      style={{
        background: isActive ? "rgba(139,92,246,0.12)" : "transparent",
        border: isActive
          ? "1px solid rgba(139,92,246,0.25)"
          : "1px solid transparent",
      }}
      onMouseEnter={(e) => {
        if (!isActive)
          e.currentTarget.style.background = "rgba(255,255,255,0.03)";
      }}
      onMouseLeave={(e) => {
        if (!isActive) e.currentTarget.style.background = "transparent";
      }}
    >
      {/* Icon */}
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{
          background: isActive
            ? "rgba(139,92,246,0.2)"
            : "rgba(255,255,255,0.05)",
        }}
      >
        {isActive ? (
          <FolderOpen size={14} className="text-violet-400" />
        ) : (
          <Folder size={14} className="text-gray-600" />
        )}
      </div>

      {/* Name + meta */}
      <div className="flex-1 min-w-0">
        {renaming ? (
          <RenameInput
            value={project.name}
            onSave={(name) => {
              onRename(project.id, name);
              setRenaming(false);
            }}
            onCancel={() => setRenaming(false)}
          />
        ) : (
          <p
            className={`text-sm font-medium truncate leading-none ${isActive ? "text-white" : "text-gray-400"}`}
          >
            {project.name}
          </p>
        )}
        <div className="flex items-center gap-2 mt-1">
          {blockCount > 0 && (
            <span className="text-[10px] text-gray-700 flex items-center gap-0.5">
              <Layers size={9} />
              {blockCount}
            </span>
          )}
          {dur && (
            <span className="text-[10px] text-gray-700 flex items-center gap-0.5">
              <Clock size={9} />
              {dur}
            </span>
          )}
          {project.backgroundMusic && (
            <span className="text-[10px] text-gray-700 flex items-center gap-0.5">
              <Music2 size={9} />
              music
            </span>
          )}
          <span className="text-[10px] text-gray-800 ml-auto">
            {formatDate(project.updatedAt || project.createdAt)}
          </span>
        </div>
      </div>

      {/* Actions */}
      {!renaming && (
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setRenaming(true);
            }}
            className="w-6 h-6 flex items-center justify-center rounded-md text-gray-600 hover:text-gray-300 transition-colors"
          >
            <Edit2 size={11} />
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="w-6 h-6 flex items-center justify-center rounded-md text-gray-600 hover:text-red-400 disabled:opacity-40 transition-colors"
          >
            {deleting ? (
              <Loader size={11} className="animate-spin" />
            ) : (
              <Trash2 size={11} />
            )}
          </button>
        </div>
      )}

      {isActive && !renaming && (
        <ChevronRight size={12} className="text-violet-500 flex-shrink-0" />
      )}
    </div>
  );
};

const ProjectsDrawer = ({ currentProjectId, onSwitch, onClose }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getVoiceProjects();
      setProjects(res.projects || []);
    } catch (e) {
      console.error("Failed to load projects:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await api.createVoiceProject({ name: "Untitled Project" });
      const project = res.project;
      setProjects((prev) => [project, ...prev]);
      onSwitch(project.id);
      onClose();
    } catch (e) {
      console.error("Failed to create project:", e);
    } finally {
      setCreating(false);
    }
  };

  const handleRename = async (id, name) => {
    setProjects((prev) => prev.map((p) => (p.id === id ? { ...p, name } : p)));
    try {
      await api.updateVoiceProject(id, { name });
    } catch {
      load();
    }
  };

  const handleDelete = async (id) => {
    setProjects((prev) => prev.filter((p) => p.id !== id));
    if (id === currentProjectId) {
      const remaining = projects.filter((p) => p.id !== id);
      if (remaining.length > 0) {
        onSwitch(remaining[0].id);
      } else {
        const res = await api.createVoiceProject({ name: "Untitled Project" });
        setProjects([res.project]);
        onSwitch(res.project.id);
      }
    }
    try {
      await api.deleteVoiceProject(id);
    } catch {
      load();
    }
  };

  const handleOpen = (id) => {
    if (id !== currentProjectId) onSwitch(id);
    onClose();
  };

  const sorted = [...projects].sort((a, b) => {
    if (a.id === currentProjectId) return -1;
    if (b.id === currentProjectId) return 1;
    return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
  });

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40"
        style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className="fixed left-0 top-0 bottom-0 w-72 z-50 flex flex-col shadow-2xl"
        style={{
          background: "linear-gradient(180deg, #0d1018 0%, #090c12 100%)",
          borderRight: "1px solid rgba(255,255,255,0.06)",
          boxShadow: "8px 0 40px rgba(0,0,0,0.6)",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-4"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}
        >
          <div>
            <h2 className="text-sm font-bold text-white tracking-tight">
              Projects
            </h2>
            {!loading && (
              <p className="text-[10px] text-gray-600 mt-0.5">
                {projects.length} project{projects.length !== 1 ? "s" : ""}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-600 hover:text-white transition-colors"
            style={{ background: "rgba(255,255,255,0.04)" }}
          >
            <X size={14} />
          </button>
        </div>

        {/* New project button */}
        <div
          className="px-3 py-3"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
        >
          <button
            onClick={handleCreate}
            disabled={creating}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-xs font-bold text-white disabled:opacity-60 transition-all"
            style={{
              background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
              boxShadow: "0 4px 12px rgba(124,58,237,0.25)",
            }}
          >
            {creating ? (
              <>
                <Loader size={12} className="animate-spin" />
                Creating…
              </>
            ) : (
              <>
                <Plus size={12} />
                New Project
              </>
            )}
          </button>
        </div>

        {/* List */}
        <div
          className="flex-1 overflow-y-auto p-2"
          style={{
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(139,92,246,0.2) transparent",
          }}
        >
          {loading ? (
            <div className="flex items-center justify-center h-24 gap-2">
              <div
                className="w-5 h-5 rounded-full animate-spin"
                style={{
                  border: "2px solid rgba(139,92,246,0.2)",
                  borderTopColor: "#7c3aed",
                }}
              />
              <span className="text-xs text-gray-600">Loading…</span>
            </div>
          ) : sorted.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-24 gap-1">
              <p className="text-xs text-gray-600">No projects yet</p>
            </div>
          ) : (
            <div className="space-y-0.5">
              {sorted.map((p) => (
                <ProjectRow
                  key={p.id}
                  project={p}
                  isActive={p.id === currentProjectId}
                  onOpen={handleOpen}
                  onRename={handleRename}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProjectsDrawer;
